import React, { useState } from 'react';
import { User, Mail, Lock, Eye, EyeOff, Trash2, AlertTriangle, Shield, Check, X, Globe } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { logSecurityEvent } from '../../../services/securityService';


const AccountSettings = ({ profileData, onUpdate, isEditing }) => {
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const [personalInfo, setPersonalInfo] = useState({
    firstName: profileData?.firstName || '',
    lastName: profileData?.lastName || '',
    email: profileData?.email || '',
    timezone: profileData?.timezone || 'America/New_York'
  });

  const { user } = useAuth();

  const validatePassword = (password) => {
    const checks = {
      length: password?.length >= 8,
      uppercase: /[A-Z]/?.test(password),
      lowercase: /[a-z]/?.test(password),
      number: /\d/?.test(password),
      special: /[!@#$%^&*]/?.test(password)
    };
    const strength = Object.values(checks)?.filter(Boolean)?.length;
    return { checks, strength, isValid: strength >= 4 };
  };

  const passwordValidation = validatePassword(passwords?.new);

  // US Timezones list
  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Phoenix', label: 'Mountain Time - Arizona (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
    { value: 'America/Puerto_Rico', label: 'Atlantic Time - Puerto Rico (AT)' }
  ];

  const handlePersonalInfoChange = (field, value) => {
    const newInfo = { ...personalInfo, [field]: value };
    setPersonalInfo(newInfo);
    if (isEditing) onUpdate?.(newInfo);
  };

  const handlePasswordChange = async () => {
    if (!passwords?.current || !passwords?.new || !passwords?.confirm) return;
    if (passwords?.new !== passwords?.confirm) return;
    if (!passwordValidation?.isValid) return;

    try {
      const { error } = await supabase?.auth?.updateUser({ password: passwords?.new });
      if (!error && user?.id) {
        logSecurityEvent(user?.id, 'password_change', 'success');
      }
    } catch (err) {
      console.warn('Password change error:', err?.message);
    }
    setPasswords({ current: '', new: '', confirm: '' });
  };

  const handleAccountDeletion = () => {
    if (deleteConfirmText === 'DELETE MY ACCOUNT') {
      console.log('Account deletion requested');
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev?.[field] }));
  };

  const inputBase = `block w-full px-4 pt-6 pb-2 border-2 rounded-xl text-foreground placeholder-transparent focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200`;
  const inputDisabled = `bg-muted border-border cursor-not-allowed opacity-60`;
  const inputEnabled = `bg-background border-border hover:border-border/80 focus:border-primary focus:shadow-[0_0_0_3px_hsl(var(--primary)/0.12)]`;

  const FloatingField = ({ id, label, type = 'text', value, onChange, disabled, placeholder = label }) => {
    const [focused, setFocused] = React.useState(false);
    const lifted = focused || Boolean(value);
    return (
      <div className="relative group">
        <div className={`absolute inset-0 rounded-xl transition-all duration-300 pointer-events-none ${focused && !disabled ? 'bg-gradient-to-r from-primary/8 via-primary/4 to-transparent opacity-100' : 'opacity-0'}`} />
        <input
          id={id}
          type={type}
          value={value}
          onChange={onChange}
          disabled={disabled}
          placeholder={placeholder}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className={`${inputBase} ${!disabled ? inputEnabled : inputDisabled} placeholder:text-transparent w-full`}
        />
        <label
          htmlFor={id}
          className={`absolute left-4 pointer-events-none select-none transition-all duration-200 ${lifted ? 'top-2 text-xs font-semibold' : 'top-1/2 -translate-y-1/2 text-sm'} ${focused && !disabled ? 'text-primary' : 'text-muted-foreground'}`}
        >
          {label}
        </label>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Personal Information */}
      <div className="bg-card rounded-2xl border border-border shadow-warm-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-bold text-foreground">Personal Information</h2>
            <p className="text-muted-foreground">Update your basic profile details</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <FloatingField
              id="firstName"
              label="First Name"
              value={personalInfo?.firstName}
              onChange={(e) => handlePersonalInfoChange('firstName', e?.target?.value)}
              disabled={!isEditing}
              placeholder="First Name"
            />
          </div>
          <div>
            <FloatingField
              id="lastName"
              label="Last Name"
              value={personalInfo?.lastName}
              onChange={(e) => handlePersonalInfoChange('lastName', e?.target?.value)}
              disabled={!isEditing}
              placeholder="Last Name"
            />
          </div>
        </div>

        <div className="mt-6">
          <div className="relative group">
            <div className={`absolute inset-0 rounded-xl transition-all duration-300 pointer-events-none opacity-0`} />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
              <Mail className="h-5 w-5 text-muted-foreground" />
            </div>
            <FloatingField
              id="email"
              label="Email Address"
              type="email"
              value={personalInfo?.email}
              onChange={(e) => handlePersonalInfoChange('email', e?.target?.value)}
              disabled={!isEditing}
              placeholder="Email Address"
            />
          </div>
        </div>

        <div className="mt-6">
          <label htmlFor="timezone" className="block text-sm font-medium text-foreground mb-2">Timezone</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Globe className="h-5 w-5 text-muted-foreground" />
            </div>
            <select
              id="timezone"
              value={personalInfo?.timezone}
              onChange={(e) => handlePersonalInfoChange('timezone', e?.target?.value)}
              disabled={!isEditing}
              className={`block w-full pl-10 pr-10 py-3 border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-base appearance-none ${!isEditing ? `bg-muted border-border cursor-not-allowed opacity-60` : `bg-background border-border`}`}
            >
              {timezones?.map((tz) => (
                <option key={tz?.value} value={tz?.value}>{tz?.label}</option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Daily review verses will become available at midnight in your selected timezone
          </p>
        </div>
      </div>

      {/* Password Management */}
      <div className="bg-card rounded-2xl border border-border shadow-warm-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="h-10 w-10 bg-warning/10 rounded-full flex items-center justify-center">
            <Lock className="w-5 h-5 text-warning" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-bold text-foreground">Password Security</h2>
            <p className="text-muted-foreground">Keep your account secure with a strong password</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Current Password */}
          <div>
            <div className="relative">
              <FloatingField
                id="currentPassword"
                label="Current Password"
                type={showPasswords?.current ? 'text' : 'password'}
                value={passwords?.current}
                onChange={(e) => setPasswords(prev => ({ ...prev, current: e?.target?.value }))}
                disabled={!isEditing}
                placeholder="Current Password"
              />
              <button type="button" onClick={() => togglePasswordVisibility('current')} disabled={!isEditing} className="absolute inset-y-0 right-0 pr-3 flex items-center z-10">
                {showPasswords?.current ? <EyeOff className="h-5 w-5 text-muted-foreground hover:text-foreground" /> : <Eye className="h-5 w-5 text-muted-foreground hover:text-foreground" />}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <div className="relative">
              <FloatingField
                id="newPassword"
                label="New Password"
                type={showPasswords?.new ? 'text' : 'password'}
                value={passwords?.new}
                onChange={(e) => setPasswords(prev => ({ ...prev, new: e?.target?.value }))}
                disabled={!isEditing}
                placeholder="New Password"
              />
              <button type="button" onClick={() => togglePasswordVisibility('new')} disabled={!isEditing} className="absolute inset-y-0 right-0 pr-3 flex items-center z-10">
                {showPasswords?.new ? <EyeOff className="h-5 w-5 text-muted-foreground hover:text-foreground" /> : <Eye className="h-5 w-5 text-muted-foreground hover:text-foreground" />}
              </button>
            </div>
          </div>

          {/* Password Strength */}
          {passwords?.new && (
            <div className="bg-muted rounded-lg p-4 space-y-3">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-foreground">Password Strength:</span>
                <span className={`text-sm font-bold ${
                  passwordValidation?.strength >= 4 ? 'text-success' :
                  passwordValidation?.strength >= 3 ? 'text-warning' : 'text-destructive'
                }`}>
                  {passwordValidation?.strength >= 4 ? 'Strong' : passwordValidation?.strength >= 3 ? 'Medium' : 'Weak'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {Object.entries(passwordValidation?.checks)?.map(([check, passed]) => (
                  <div key={check} className="flex items-center space-x-2">
                    {passed ? <Check className="w-3 h-3 text-success" /> : <X className="w-3 h-3 text-destructive" />}
                    <span className={passed ? 'text-success' : 'text-destructive'}>
                      {check === 'length' && '8+ characters'}
                      {check === 'uppercase' && 'Uppercase letter'}
                      {check === 'lowercase' && 'Lowercase letter'}
                      {check === 'number' && 'Number'}
                      {check === 'special' && 'Special character'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Confirm Password */}
          <div>
            <div className="relative">
              <FloatingField
                id="confirmPassword"
                label="Confirm New Password"
                type={showPasswords?.confirm ? 'text' : 'password'}
                value={passwords?.confirm}
                onChange={(e) => setPasswords(prev => ({ ...prev, confirm: e?.target?.value }))}
                disabled={!isEditing}
                placeholder="Confirm New Password"
              />
              <button type="button" onClick={() => togglePasswordVisibility('confirm')} disabled={!isEditing} className="absolute inset-y-0 right-0 pr-3 flex items-center z-10">
                {showPasswords?.confirm ? <EyeOff className="h-5 w-5 text-muted-foreground hover:text-foreground" /> : <Eye className="h-5 w-5 text-muted-foreground hover:text-foreground" />}
              </button>
            </div>
          </div>

          {passwords?.new && passwords?.confirm && passwords?.new !== passwords?.confirm && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <X className="w-4 h-4 text-destructive" />
                <p className="text-sm text-destructive">Passwords do not match</p>
              </div>
            </div>
          )}

          {isEditing && (
            <button
              onClick={handlePasswordChange}
              disabled={!passwords?.current || !passwords?.new || !passwords?.confirm || passwords?.new !== passwords?.confirm || !passwordValidation?.isValid}
              className="w-full flex justify-center items-center space-x-2 py-3 px-4 rounded-lg text-base font-medium text-primary-foreground bg-warning hover:bg-warning/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-warning transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Shield className="w-4 h-4" />
              <span>Update Password</span>
            </button>
          )}
        </div>
      </div>

      {/* Account Deletion */}
      <div className="bg-card rounded-2xl border border-border border-l-4 border-l-destructive shadow-warm-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="h-10 w-10 bg-destructive/10 rounded-full flex items-center justify-center">
            <Trash2 className="w-5 h-5 text-destructive" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-bold text-destructive">Danger Zone</h2>
            <p className="text-destructive/80">Permanently delete your account and all associated data</p>
          </div>
        </div>

        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
            <div className="text-sm text-destructive">
              <p className="font-semibold mb-2">This action cannot be undone!</p>
              <ul className="space-y-1 text-destructive/80">
                <li>• All your saved verses and reflections will be deleted</li>
                <li>• Your reading progress and statistics will be lost</li>
                <li>• Community contributions will be anonymized</li>
                <li>• Account recovery will not be possible</li>
              </ul>
            </div>
          </div>
        </div>

        {!showDeleteConfirm ? (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={!isEditing}
            className={`flex items-center space-x-2 px-4 py-2 border border-destructive/40 rounded-lg text-destructive font-medium transition-colors ${isEditing ? 'hover:bg-destructive/10 hover:border-destructive' : 'opacity-50 cursor-not-allowed'}`}
          >
            <Trash2 className="w-4 h-4" />
            <span>Delete Account</span>
          </button>
        ) : (
          <div className="space-y-4">
            <div>
              <label htmlFor="deleteConfirm" className="block text-sm font-medium text-destructive mb-2">
                Type "DELETE MY ACCOUNT" to confirm deletion:
              </label>
              <input
                id="deleteConfirm"
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e?.target?.value)}
                className="block w-full px-3 py-2 border border-destructive/40 rounded-lg text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-destructive focus:border-destructive"
                placeholder="DELETE MY ACCOUNT"
              />
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }}
                className="px-4 py-2 border border-border rounded-lg text-muted-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAccountDeletion}
                disabled={deleteConfirmText !== 'DELETE MY ACCOUNT'}
                className="px-4 py-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Trash2 className="w-4 h-4" />
                <span>Permanently Delete Account</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountSettings;